import React, { useState } from "react";
import {
  Box,
  Grid,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";

import Layout from "@components/Layout";
import Title from "@components/Title";
import ModalInactivatedUser from "@components/ModalInactivatedUser";
import PaginationDynamic from "@components/PaginationDynamic/PaginationDynamic";
import { IUser } from "@utils/interfaces";

import { useListUsers } from "./hook/useListUsers";
import FilterListUsers from "./ui/FilterDrawer";
import ModalListBusinessName from "./ui/UserDetailsModal";
import SkeletonTableListUsers from "./ui/ListUsersSkeleton";
import UserItem from "./ui/UserItem";
import { HEADER_TABLE } from "./constants";

const headerSx = {
  backgroundColor: "#FFF",
  border: "none",
  fontSize: "1.4rem",
  color: "#3E3D3D",
  fontWeight: "bold",
};

const ListUsersView: React.FC = () => {
  const {
    loadingUsers,
    paginatedUsers,
    totalItems,
    totalPages,
    page,
    setPage,
    active,
    tabIndex,
    handleTabChange,
    showBlockModal,
    setShowBlockModal,
    selectedUserId,
    setSelectedUserId,
    showDetailsModal,
    setShowDetailsModal,
    selectedUser,
    setSelectedUser,
    handleBlockUser,
    handleUnlockUser,
    handleResetPassword,
    resetLoading,
    throttledSearch,
  } = useListUsers();

  const [drawer, setDrawer] = useState(false);

  const userItemHandlers = (user: IUser) => ({
    onDetails: () => {
      setShowDetailsModal(true);
      setSelectedUser(user);
    },
    onToggleStatus: () => {
      setShowBlockModal(true);
      setSelectedUserId(user.id);
    },
  });

  return (
    <>
      {showBlockModal && (
        <ModalInactivatedUser
          showModal={() => setShowBlockModal(false)}
          blockUser={handleBlockUser}
          unlockUser={handleUnlockUser}
          userID={selectedUserId}
          enabled={active}
        />
      )}
      {showDetailsModal && selectedUser && (
        <ModalListBusinessName
          business={showDetailsModal}
          handle={() => setShowDetailsModal(false)}
          user={selectedUser}
          loading={resetLoading}
          onResetPassword={handleResetPassword}
        />
      )}

      <Layout disableToolbar disableToolbarMobile>
        <Title title="Usuários" />

        {loadingUsers && <SkeletonTableListUsers />}

        {!loadingUsers && (
          <Grid
            container
            alignItems="center"
            spacing={2}
            sx={{ p: { xs: "1rem", sm: "1rem 5rem" }, borderRadius: 1 }}
          >
            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              <Tabs
                value={tabIndex}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab
                  sx={{ textTransform: "capitalize", fontSize: "1.4rem" }}
                  label="Ativos"
                />
                <Tab
                  sx={{ textTransform: "capitalize", fontSize: "1.4rem" }}
                  label="Inativos"
                />
              </Tabs>
              <Typography
                sx={{
                  color: "#D91F05",
                  fontWeight: 600,
                  cursor: "pointer",
                  alignSelf: "center",
                  fontSize: "1.4rem",
                }}
                onClick={() => setDrawer(true)}
              >
                Filtrar
              </Typography>
              <FilterListUsers
                drawer={drawer}
                handle={() => setDrawer(false)}
                throttle={throttledSearch}
                initialPage={setPage}
              />
            </Grid>
          </Grid>
        )}

        {/* Desktop table */}
        {!loadingUsers && (
          <Box
            sx={{
              display: { xs: "none", sm: "block" },
              m: "0 0 0 5rem",
              width: "93%",
              overflowX: "auto",
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  {HEADER_TABLE.map((h) => (
                    <TableCell key={h} sx={headerSx}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.map((user) => (
                  <UserItem
                    key={user.id}
                    user={user}
                    {...userItemHandlers(user)}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Mobile cards */}
        {!loadingUsers && (
          <Box sx={{ display: { xs: "block", sm: "none" }, px: 1 }}>
            {paginatedUsers.map((user) => (
              <UserItem key={user.id} user={user} {...userItemHandlers(user)} />
            ))}
          </Box>
        )}

        {!loadingUsers && totalItems > 0 && (
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            sx={{ p: { xs: "8px 1rem", sm: "5px 48px 15px" } }}
          >
            <Typography
              sx={{ fontWeight: 600, color: "#3E3D3D", fontSize: "1.3rem" }}
            >
              Mostrando {paginatedUsers.length} de {totalItems}
            </Typography>
            <PaginationDynamic
              onPageChange={(_, p) => setPage(p)}
              pagesTotal={totalPages}
              page={page + 1}
            />
          </Grid>
        )}
      </Layout>
    </>
  );
};

export default ListUsersView;
